'use client';

import Link from 'next/link';
import Logo, { Api } from '@/components/icons/Logo';
import s from './Navbar.module.css';
import {
  TONE_ZONE_ANSI,
  FORMAT,
  GEAR_DISPLAY_ORDER,
  GEAR_TITLE_MAP,
  SWEEP_CAPTURE_GUIDE_URL,
} from '@/utils/constants';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
  type CSSProperties,
  type KeyboardEventHandler,
  type ReactNode,
} from 'react';
import { EmailModal } from '@/components/ui/EmailModal/EmailModal';
import { handleRequest, handleSignOut } from '@/utils/auth-helpers/client';
import { SignOut } from '@/utils/auth-helpers/server';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useOAuthFlow, useOAuthHref, OAuthLink } from '@/contexts/OAuthFlowContext';
import ProfileAvatar from '../ProfileUser/ProfileAvatar';
import {
  Settings,
  LogOut,
  Plus,
  AudioLines,
  Upload,
  Bell,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button, LinkButton } from '../Button/Button';
import SearchInput from '../SearchInput/SearchInput';
import { Discord, Facebook, Github, TikTok, Youtube, Instagram } from '@/components/icons/Social';
import { GearIcon } from '@/components/icons/Gear';
import { Varified } from '@/components/icons/Varified';
import FormatPill from '../Tones/FormatPill';
import type { NavCreator, NavPost } from './nav-data';
import { panelOffset, PANEL_WIDTH } from './panel-offset';
import { useScrollFade, scrollFadeMask } from '@/hooks/useScrollFade';
import { createClient } from '@/utils/supabase/client';
import { hasUnreadNotifications as hasUnreadNotificationsQuery } from '@/utils/supabase/queries';
import { getURL } from '@/utils/helpers';
import { HalloweenBat, useIsHalloween } from '../Halloween/Halloween';

const HamburgerSvg = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 12H20"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M4 6H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M4 18H20"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface NavlinksProps {
  userDetails?: any;
  isApiSelect?: boolean;
  isOAuthFlow?: boolean;
  navCreators?: NavCreator[];
  navPosts?: NavPost[];
}

console.log(TONE_ZONE_ANSI);

const HamburgerMenu = ({ userDetails, isApiSelect, isOAuthFlow }: NavlinksProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const menuRef = useRef(null);
  const router = useRouter();
  const t = useTranslations('nav');
  const { authorizationId } = useOAuthFlow();
  const supabase = createClient();

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      // @ts-ignore
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch unread notifications status
  const fetchUnreadStatus = async () => {
    if (userDetails?.id) {
      const unreadStatus = await hasUnreadNotificationsQuery(supabase, userDetails.id);
      setHasUnreadNotifications(unreadStatus);
    }
  };

  useEffect(() => {
    fetchUnreadStatus();
  }, [userDetails?.id]);

  // Listen for custom events to refresh unread status
  useEffect(() => {
    const handleRefreshUnread = () => {
      fetchUnreadStatus();
    };

    window.addEventListener('refreshUnreadNotifications', handleRefreshUnread);
    return () => window.removeEventListener('refreshUnreadNotifications', handleRefreshUnread);
  }, [userDetails?.id]);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleOAuthSignOut = async () => {
    handleLinkClick();
    // Resolve partner redirect URL before signing out. /select/cancel accepts
    // authorization_id from the body — must happen while session is still active.
    // Strip the code param; the user is signing out, not completing the flow.
    let partnerRedirectUrl: string | null = null;
    try {
      const res = await fetch('/api/v1/select/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ authorization_id: authorizationId }),
      });
      const data = await res.json();
      if (data.redirect_url) {
        const url = new URL(data.redirect_url);
        url.searchParams.delete('code');
        partnerRedirectUrl = url.toString();
      }
    } catch (err) {
      console.error('[handleOAuthSignOut] Cancel request failed:', err);
    }

    // Fire client-side sign-out first so providers.tsx onAuthStateChange
    // emits auth.signed_out before the server action redirects.
    await supabase.auth.signOut({ scope: 'local' });
    const formData = new FormData();
    formData.set('pathName', '/api/v1/select');
    await SignOut(formData);

    // Hard navigation in both branches: client signOut + server action above
    // clears cookies via fetch-not-action, which doesn't bust the RSC cache
    // that router.push relies on. Hard nav forces a fresh request that
    // renders the destination with no session.
    window.location.assign(partnerRedirectUrl ?? '/');
  };

  const renderApiSelectMenu = () => (
    <>
      {userDetails ? (
        <>
          <OAuthLink
            href={`/api/v1/select/${userDetails.username}`}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <ProfileAvatar
              username={userDetails?.username}
              avatarUrl={userDetails?.avatar_url}
              size={20}
              ownProfile={false}
            />
            {t('profileAndTones')}
          </OAuthLink>
          <button
            type="button"
            onClick={handleOAuthSignOut}
            className={`text-left w-full ${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
          >
            <LogOut size={20} />
            {t('logOut')}
          </button>
        </>
      ) : (
        <>
          <OAuthLink
            href={`/api/v1/select/signin/email`}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
            onClick={handleLinkClick}
          >
            {t('logIn')}
          </OAuthLink>
          <OAuthLink
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
            href={`/api/v1/select/signin/email`}
            onClick={handleLinkClick}
          >
            {t('signUp')}
          </OAuthLink>
        </>
      )}
    </>
  );

  const renderCoreMenu = () => (
    <>
      {userDetails ? (
        <>
          <Link
            href={`/${userDetails.username}`}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <ProfileAvatar
              username={userDetails?.username}
              avatarUrl={userDetails?.avatar_url}
              size={20}
              ownProfile={false}
            />
            Profile & Tones
          </Link>
          <Link
            href="/notifications"
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <div className="relative">
              <Bell size={20} />
              {hasUnreadNotifications && (
                <div className="absolute -top-0 -right-0 w-2 h-2 bg-[#ff0000] rounded-full"></div>
              )}
            </div>
            Notifications
          </Link>
          <Link
            href={`/settings`}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <Settings size={20} />
            Settings
          </Link>
          <form
            className={'flex justify-center'}
            onSubmit={(e) => handleSignOut(e)}
          >
            <input type="hidden" name="pathName" value={'/'} />
            <button
              type="submit"
              onClick={handleLinkClick}
              className={`text-left w-full ${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg text-left flex flex-row items-center gap-4 whitespace-nowrap`}
            >
              <LogOut size={20} />
              Log Out
            </button>
          </form>
        </>
      ) : (
        <>
          <Link
            href="/signin/email"
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
            onClick={handleLinkClick}
          >
            Log In
          </Link>
          <Link
            href="/signin/email"
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
            onClick={handleLinkClick}
          >
            Sign Up
          </Link>
        </>
      )}
      <hr className="border-zinc-500 border-opacity-50 mx-3 my-2" />
      <Link
        href="/capture"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
        onClick={handleLinkClick}
      >
        Capture Your Gear
      </Link>
      <Link
        href={SWEEP_CAPTURE_GUIDE_URL}
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
        onClick={handleLinkClick}
      >
        Capture Instructions
      </Link>
      <EmailModal
        tracking={{
          event: 'contact.email_modal.opened',
        }}
        trigger={
          <span className={`text-left w-full ${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}>
            Contact
          </span>
        }
        message="Email us and we'll respond ASAP."
        title="Contact T3K"
      />
      <hr className="border-zinc-500 border-opacity-50 mx-3 my-2" />
      <Link
        href="https://github.com/tone-3000"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
      >
        <Github />
        Github
      </Link>
      <Link
        href="https://discord.gg/wEV8uEWDnb"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
        onClick={handleLinkClick}
      >
        <Discord />
        Discord
      </Link>
      <Link
        href="https://www.instagram.com/tone3000"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
      >
        <Instagram />
        Instagram
      </Link>
      <Link
        href="https://www.tiktok.com/@tone30000"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
      >
        <TikTok />
        TikTok
      </Link>
      <Link
        href="https://www.youtube.com/@tone3000-nam"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
      >
        <Youtube />
        Youtube
      </Link>
      <Link
        href="https://www.facebook.com/groups/neuralampmodeler"
        target="_blank"
        rel="noopener noreferrer"
        className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg`}
      >
        <Facebook />
        NAM Facebook Group
      </Link>
    </>
  );

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="pl-4 pr-1.5 py-2 text-white focus:outline-none flex flex-row items-center gap-3 rounded-full border border-zinc-700"
      >
        <>
          {HamburgerSvg}
          <div className="relative">
            <ProfileAvatar
              username={userDetails?.username}
              avatarUrl={userDetails?.avatar_url}
              size={32}
              ownProfile={false}
            />
            {hasUnreadNotifications && (
              <div className="absolute -top-0 -right-0 w-2 h-2 bg-[#ff0000] rounded-full"></div>
            )}
          </div>
        </>
      </button>
      <nav
        className={`${isOpen ? 'block' : 'hidden'} absolute z-50 right-0 top-[60px] w-[255px] bg-black text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50 bg-zinc-900`}
      >
        <div className="flex flex-col p-3">
          {isApiSelect || isOAuthFlow ? renderApiSelectMenu() : renderCoreMenu()}
        </div>
      </nav>
    </div>
  );
};

const CreateMenu = ({ userDetails }: NavlinksProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      // @ts-ignore
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative hidden md:block" ref={menuRef}>
      <Button
        isSecondary
        variant="slim"
        style={{
          borderRadius: '100px',
          padding: '0 14px',
          height: '50px',
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Plus size={20} />
        Create Tone
      </Button>
      <nav
        className={`${isOpen ? 'block' : 'hidden'} absolute z-50 right-0 top-[60px] w-78 bg-black text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50 bg-zinc-900`}
      >
        <div className="flex flex-col p-3">
          <Link
            href={!!userDetails ? `/create/capture` : '/capture'}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <AudioLines size={20} className="flex-shrink-0" />
            <span className="flex flex-col">
              Capture
              <span className="text-zinc-400 text-sm">Capture a digital model of your gear.</span>
            </span>
          </Link>
          <hr className="border-zinc-500 border-opacity-50 mx-3 my-2" />
          <Link
            href={!!userDetails ? `/create/upload` : '/upload'}
            className={`${s.link} py-2 px-3 hover:bg-zinc-800 rounded-lg flex flex-row items-center gap-4 whitespace-nowrap`}
            onClick={handleLinkClick}
          >
            <Upload size={20} className="flex-shrink-0" />
            <span className="flex flex-col">
              Upload
              <span className="text-zinc-400 text-sm">Upload NAM or IR files.</span>
            </span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

type SubNavLink = {
  key: string;
  label: ReactNode;
  href: string;
  icon?: ReactNode;
  // Sets a rule below this row, to hold a pinned item apart from the rest.
  dividerAfter?: boolean;
};

// An entry is either a single destination or a dropdown group, never both.
// `name` stays separate from `label` because `label` is a ReactNode and can't
// feed an aria-label.
type SubNavEntry =
  | { kind: 'link'; name: string; label: ReactNode; href: string }
  | { kind: 'divider'; name: string }
  | {
      kind: 'group';
      name: string;
      label: ReactNode;
      // Heads the panel, and doesn't always match the trigger: "NAM & IR" opens
      // "Format", "Creators" opens "Top Creators".
      title: string;
      items: SubNavLink[];
      viewAll?: { href: string; label: string };
    };

// Shortened for the row, so these are deliberately not the guides' published
// Sanity titles.
const GUIDES: { key: string; title: string; slug: string }[] = [
  { key: 'tone3000-plugin', title: 'TONE3000 Plugin', slug: 'tone3000-plugin' },
  { key: 'neural-amp-modeler', title: 'Neural Amp Modeler (NAM)', slug: 'neural-amp-modeler' },
  { key: 'nam-a2', title: 'NAM A2: Complete Guide', slug: 'nam-a2-the-complete-guide' },
  { key: 'impulse-responses', title: 'Impulse Responses (IRs)', slug: 'impulse-responses' },
  {
    key: 'nam-sweep',
    title: 'How to capture with NAM',
    slug: 'capture-your-gear-with-nam-sweep-method',
  },
];

const GUIDE_ITEMS: SubNavLink[] = GUIDES.map(({ key, title, slug }) => ({
  key,
  label: <span className="line-clamp-3 min-w-0">{title}</span>,
  href: `/guides/${slug}`,
}));

const PLUGIN_ITEMS: SubNavLink[] = [
  { key: 'plugin', label: 'TONE3000 Plugin', href: '/plugin' },
  { key: 'plugin-download', label: 'Download Plugin', href: '/plugin/download', dividerAfter: true },
  { key: 'plugin-guide', label: 'Plugin Guide', href: '/guides/tone3000-plugin' },
];

const CAPTURE_ITEMS: SubNavLink[] = [
  { key: 'capture', label: 'Create NAM Captures', href: '/capture' },
  { key: 'upload', label: 'Upload NAM Captures & IRs', href: '/upload', dividerAfter: true },
  { key: 'instructions', label: 'How to capture with NAM', href: SWEEP_CAPTURE_GUIDE_URL },
];

const GEAR_ITEMS: SubNavLink[] = GEAR_DISPLAY_ORDER.map((gear) => ({
  key: gear,
  label: GEAR_TITLE_MAP[gear],
  href: `/search?gears=${gear}`,
  icon: <GearIcon gear={gear} size={20} inheritColor />,
}));

// Fixed-width slot so the two labels line up under each other, since the pills
// themselves are as wide as their text. Flex, so the pill sizes to its text
// rather than stretching across the slot.
const formatPill = (format: FORMAT) => (
  <span className="w-10 flex-shrink-0 flex items-center">
    <FormatPill format={format} />
  </span>
);

const FORMAT_ITEMS: SubNavLink[] = [
  {
    key: FORMAT.NAM,
    label: 'NAM Captures',
    href: `/search?format=${FORMAT.NAM}`,
    icon: formatPill(FORMAT.NAM),
  },
  {
    key: FORMAT.IR,
    label: 'Impulse Responses',
    href: `/search?format=${FORMAT.IR}`,
    icon: formatPill(FORMAT.IR),
  },
];

const creatorItems = (creators: NavCreator[]): SubNavLink[] =>
  creators.map((creator) => {
    const isVarified = creator.isVerified;
    const displayName = creator.displayName || creator.username;
    return {
      key: creator.username,
      href: `/${creator.username}`,
      icon: (
        <span className="flex-shrink-0">
          <ProfileAvatar
            username={creator.username}
            avatarUrl={creator.avatarUrl}
            size={20}
            ownProfile={false}
          />
        </span>
      ),
      label: (
        <span className="flex flex-row items-center gap-1.5 min-w-0">
          <span className="truncate">{displayName}</span>
          {isVarified && <Varified size={16} className="flex-shrink-0" />}
        </span>
      ),
    };
  });

// An empty data-driven group would hold nothing but its own View All, so it
// collapses to a plain link to the same destination.
const groupOrLink = (
  name: string,
  title: string,
  items: SubNavLink[],
  viewAll: { href: string; label: string }
): SubNavEntry =>
  items.length
    ? { kind: 'group', name, label: name, title, items, viewAll }
    : { kind: 'link', name, label: name, href: viewAll.href };

const buildSubNavEntries = (creators: NavCreator[], posts: NavPost[]): SubNavEntry[] => [
  {
    kind: 'link',
    name: 'Browse',
    label: 'Browse',
    href: '/search',
  },
  {
    kind: 'group',
    name: 'Gear Type',
    label: 'Gear Type',
    title: 'Gear Type',
    items: GEAR_ITEMS,
    viewAll: { href: '/search', label: 'All Gear' },
  },
  {
    kind: 'group',
    name: 'NAM & IR',
    label: 'NAM & IR',
    title: 'Format',
    items: FORMAT_ITEMS,
    viewAll: { href: '/search', label: 'All Formats' },
  },
  groupOrLink('Creators', 'Top Creators', creatorItems(creators), {
    href: '/top-creators',
    label: 'All Creators',
  }),
  { kind: 'divider', name: 'divider' },
  {
    kind: 'group',
    name: 'Plugin',
    label: 'Plugin',
    title: 'Plugin',
    items: PLUGIN_ITEMS,
  },
  {
    kind: 'group',
    name: 'Guides',
    label: 'Guides',
    title: 'Guides',
    items: GUIDE_ITEMS,
    viewAll: { href: '/guides', label: 'All Guides' },
  },
  groupOrLink(
    'Blog',
    'Blog',
    posts.map((post) => ({
      key: post.slug,
      label: <span className="line-clamp-3 min-w-0">{post.title}</span>,
      href: `/blog/${post.slug}`,
      // Nothing to rule off against if the pinned post is all we have.
      dividerAfter: !!post.pinned && posts.length > 1,
    })),
    { href: '/blog', label: 'View Blog' }
  ),
  {
    kind: 'group',
    name: 'Capture',
    label: 'Capture',
    title: 'Capture',
    items: CAPTURE_ITEMS,
  },
  { kind: 'link', name: 'About', label: 'About', href: '/about' },
  { kind: 'link', name: 'API', label: 'API', href: '/api' },
];

// Shared by the desktop per-trigger panel and the mobile shared panel, so the
// two can't drift.
const SubNavPanelItems = ({
  entry,
}: {
  entry: Extract<SubNavEntry, { kind: 'group' }>;
}) => {
  // Row classes are spelled out rather than layered on s.linkSub, whose @apply
  // carries a gap that would compete with these at equal specificity.
  return (
    <div className="flex flex-col p-3">
      <p className="px-3 pt-1 pb-2 text-sm font-bold text-zinc-400">{entry.title}</p>
      {entry.items.map((item) => (
        <Fragment key={item.key}>
          <Link
            href={item.href}
            className="py-2 px-3 flex flex-row items-center gap-4 min-w-0 leading-6 font-medium text-white rounded-lg hover:bg-zinc-800 transition ease-in-out duration-75"
          >
            {item.icon}
            {item.label}
          </Link>
          {item.dividerAfter && (
            <div className="my-2 h-px bg-zinc-500 bg-opacity-50" aria-hidden />
          )}
        </Fragment>
      ))}
      {entry.viewAll && (
        <LinkButton href={entry.viewAll.href} isSecondary className="mt-4">
          {entry.viewAll.label}
        </LinkButton>
      )}
    </div>
  );
};

const panelDomId = (name: string) => `subnav-panel-${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

// Matches in DOM order, which is tab order as long as nothing uses a positive
// tabindex.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SubNavTrigger = ({
  entry,
  isOpen,
  onToggle,
  onKeyDown,
  registerTrigger,
}: {
  entry: Extract<SubNavEntry, { kind: 'group' }>;
  isOpen: boolean;
  onToggle: () => void;
  onKeyDown: KeyboardEventHandler<HTMLButtonElement>;
  registerTrigger: (el: HTMLButtonElement | null) => void;
}) => (
  // Same classes as a plain row link, so a trigger is only distinguished by its
  // chevron.
  <button
    type="button"
    ref={registerTrigger}
    onClick={onToggle}
    onKeyDown={onKeyDown}
    // No aria-haspopup: that announces a menu, and the panel is a list of links
    // with no menu keys behind it. Button + aria-expanded + aria-controls is
    // what this actually is.
    aria-expanded={isOpen}
    aria-controls={panelDomId(entry.name)}
    aria-label={entry.name}
    className={`${s.linkSub} text-sm flex flex-shrink-0 flex-row items-center whitespace-nowrap`}
  >
    {entry.label}
    <ChevronDown size={16} className="flex-shrink-0" />
  </button>
);

const SubNav = ({ creators, posts }: { creators: NavCreator[]; posts: NavPost[] }) => {
  // Rebuilding this mints a fresh element per creator and per post, and every
  // open, close and resize re-renders us, so it's keyed to the data instead.
  const entries = useMemo(() => buildSubNavEntries(creators, posts), [creators, posts]);
  // Open state lives here, not per-menu, because the panels render outside the
  // scroll container (which would clip them) and so can't be children of the
  // triggers they belong to.
  const [openKey, setOpenKey] = useState<string | null>(null);
  // Container-relative left, in px, per group. Empty on the server and on first
  // paint, so the markup hydrates as plain left-alignment.
  const [offsets, setOffsets] = useState<Record<string, number>>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const panelRefs = useRef<Record<string, HTMLElement | null>>({});
  const fade = useScrollFade([entries.length]);
  const fadeMask = scrollFadeMask(fade.atStart, fade.atEnd);

  const close = useCallback(() => setOpenKey(null), []);

  // A panel renders outside the scroller, so it never follows its own trigger in
  // the tab order. These read the two ends of each list to splice it back in.
  // Every focusable, not just the links: a button added at either end of a panel
  // would otherwise sit outside the boundary checks and never take focus.
  const panelFocusables = (name: string) =>
    Array.from(panelRefs.current[name]?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
  const rowItems = () =>
    Array.from(fade.ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);

  // Tab off an open trigger lands on the panel it just opened.
  const onTriggerKeyDown =
    (name: string): KeyboardEventHandler<HTMLButtonElement> =>
    (event) => {
      if (event.key !== 'Tab' || event.shiftKey || openKey !== name) return;
      const first = panelFocusables(name)[0];
      if (!first) return;
      event.preventDefault();
      first.focus();
    };

  // And the panel's own ends hand focus back to the row: Shift+Tab off the first
  // item returns to the trigger, Tab off the last continues past it.
  const onPanelKeyDown =
    (name: string): KeyboardEventHandler<HTMLElement> =>
    (event) => {
      if (event.key !== 'Tab') return;
      const focusables = panelFocusables(name);
      const trigger = triggerRefs.current[name];
      if (!trigger) return;
      if (event.shiftKey) {
        if (event.target !== focusables[0]) return;
        event.preventDefault();
        trigger.focus();
        return;
      }
      if (event.target !== focusables[focusables.length - 1]) return;
      const items = rowItems();
      const next = items[items.indexOf(trigger) + 1];
      // No next item means focus leaves the sub-nav entirely; the focus-out
      // handler closes the panel on its way out.
      if (!next) return;
      event.preventDefault();
      close();
      next.focus();
    };

  const groups = entries.filter(
    (entry): entry is Extract<SubNavEntry, { kind: 'group' }> => entry.kind === 'group'
  );

  // Desktop panels are left-aligned to their trigger until that would push them
  // off screen, then pulled left just far enough to fit. Measured rather than
  // assigned per entry, because where a trigger sits depends on how the row
  // wraps, which depends on the viewport.
  const align = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const rootLeft = root.getBoundingClientRect().left;
    const viewportWidth = document.documentElement.clientWidth;
    const next: Record<string, number> = {};
    for (const [name, trigger] of Object.entries(triggerRefs.current)) {
      if (!trigger) continue;
      const triggerLeft = trigger.getBoundingClientRect().left;
      next[name] =
        triggerLeft - rootLeft + panelOffset({ triggerLeft, viewportWidth });
    }
    setOffsets(next);
  }, []);

  useEffect(() => {
    align();
    window.addEventListener('resize', align);
    return () => window.removeEventListener('resize', align);
  }, [align, entries.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpenKey(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="flex w-full border-t border-zinc-500 border-opacity-50">
      <div
        className="max-w-6xl w-full px-3 sm:px-6 mx-auto relative"
        ref={rootRef}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !openKey) return;
          triggerRefs.current[openKey]?.focus();
          close();
        }}
        // Catches every link in the row and in the panels, so a panel never
        // outlives the navigation it started — this component sits in the
        // layout and survives it.
        onClick={(event) => {
          if (event.target instanceof Element && event.target.closest('a')) close();
        }}
        onBlur={(event) => {
          if (!rootRef.current?.contains(event.relatedTarget)) close();
        }}
      >
        {/* Below `subnav` this scrolls horizontally; at `subnav`+ it wraps and
            overflow goes visible so the per-trigger panels aren't clipped. The
            mask is tied to actual overflow rather than a breakpoint, which
            keeps it off at `subnav`+ where a wrapped row never overflows —
            important because a mask applies to descendants and would fade an
            open panel with it. */}
        <div
          ref={fade.ref}
          onScroll={fade.onScroll}
          style={fadeMask}
          className="flex flex-nowrap subnav:flex-wrap subnav:justify-between gap-x-6 sm:gap-x-4 overflow-x-auto subnav:overflow-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        >
          {entries.map((entry) => {
            if (entry.kind === 'divider') {
              return (
                <span
                  key={entry.name}
                  aria-hidden
                  className="self-stretch w-px bg-zinc-500 bg-opacity-50 flex-shrink-0"
                />
              );
            }
            if (entry.kind === 'group') {
              return (
                <SubNavTrigger
                  entry={entry}
                  key={entry.name}
                  isOpen={openKey === entry.name}
                  onToggle={() => {
                    // Re-measure on the way open: the row wraps at `subnav` and
                    // scrolls below it, so a trigger's x can move after mount.
                    align();
                    setOpenKey(openKey === entry.name ? null : entry.name);
                  }}
                  onKeyDown={onTriggerKeyDown(entry.name)}
                  registerTrigger={(el) => {
                    triggerRefs.current[entry.name] = el;
                  }}
                />
              );
            }
            return (
              <Link
                href={entry.href}
                className={`${s.linkSub} text-sm flex flex-shrink-0 items-center whitespace-nowrap`}
                key={entry.name}
              >
                {entry.label}
              </Link>
            );
          })}
        </div>
        {/* Every panel stays in the DOM so crawlers and agents parse the whole
            taxonomy from the served HTML; only a display class flips. Rendered
            here, outside the scroll container, since overflow-x would clip them
            below `subnav`. Full-bleed while the row scrolls — a trigger's x is
            meaningless then — and offset under its trigger at `subnav`+. */}
        {groups.map((entry) => (
          <nav
            key={entry.name}
            id={panelDomId(entry.name)}
            aria-label={entry.name}
            ref={(el) => {
              panelRefs.current[entry.name] = el;
            }}
            onKeyDown={onPanelKeyDown(entry.name)}
            style={
              {
                // Fed through custom properties so the width the offset math
                // assumes and the width the panel renders at stay one value.
                '--panel-left': `${offsets[entry.name] ?? 0}px`,
                '--panel-width': `${PANEL_WIDTH}px`,
              } as CSSProperties
            }
            className={`${openKey === entry.name ? 'block' : 'hidden'} absolute z-50 top-full left-3 right-3 sm:left-6 sm:right-6 subnav:left-[var(--panel-left)] subnav:right-auto subnav:w-[var(--panel-width)] bg-zinc-900 text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50`}
          >
            <SubNavPanelItems entry={entry} />
          </nav>
        ))}
      </div>
    </div>
  );
};

export default function Navlinks({
  userDetails,
  isApiSelect,
  isOAuthFlow,
  navCreators = [],
  navPosts = [],
}: NavlinksProps) {
  const pathname = usePathname();
  // From the live pathname, not a prop: the layout survives soft nav out of /api.
  const isApiDocs = pathname === '/api';
  // OAuth login pages: searching would navigate away from the sign-in step.
  const isOAuthSignin = pathname?.includes('/api/v1/select/signin');
  // Any sign-in step. The sub-nav is a browsing tree with no use mid-auth.
  const isSignin = isOAuthSignin || pathname?.startsWith('/signin');
  // Kept out of the API select flow, which renders inside third-party sign-in.
  const isSpooky = useIsHalloween() && !isApiSelect;

  if (isApiDocs) {
    return (
      <div className="relative py-3 align-center nav:py-4 gap-8">
        <div className="max-w-6xl w-full px-3 sm:px-6 mx-auto flex flex-row justify-between">
          <div className="flex items-center">
            <Link href="/" className={s.logo} aria-label="Logo">
              <Logo />
              <span className="hidden lg:block">
                <Api />
              </span>
            </Link>
          </div>
          <HamburgerMenu userDetails={userDetails} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="max-w-6xl w-full px-3 sm:px-6 mx-auto">
        <div className="relative flex flex-row justify-between py-3 align-center nav:py-4 gap-8">
          <div className="flex items-center">
            <OAuthLink
              href={isApiSelect ? '/api/v1/select' : '/'}
              className={`${s.logo} relative`}
              aria-label="Logo"
            >
              <Logo />
              {isSpooky && <HalloweenBat />}
              {/* <span className="hidden lg:block">
                <Beta />
              </span> */}
            </OAuthLink>
          </div>
          {!isOAuthSignin && (
            <div className="flex items-center flex-1 hidden md:flex">
              <Suspense fallback={null}>
                <SearchInput isApiSelect={isApiSelect} />
              </Suspense>
            </div>
          )}
          <div className="justify-end flex flex-row items-center gap-8">
            {!isApiSelect && <CreateMenu userDetails={userDetails} />}
            <HamburgerMenu userDetails={userDetails} isApiSelect={isApiSelect} isOAuthFlow={isOAuthFlow} />
          </div>
        </div>
      </div>
      {!isApiSelect && !isSignin && <SubNav creators={navCreators} posts={navPosts} />}
    </div>
  );
}
